<template>
  <CopyTrackWrapper
    type="superpage"
    :title="pageContent.title"
    :uuid="pageContent.uuid"
    :demo="pageContent.demo"
    :class="['content-page', { 'anchor-page': hasAccordionObjectives }]"
  >
    <PremiumRequired v-if="premiumBlock" />
    <ErrorPage
      v-else-if="goalLoadError"
      msg="There was an error loading the goal. Please refresh and try again."
    />
    <div v-else class="container">
      <ContentPageBreadcrumbs
        v-if="pageContent.uuid"
        :subject="breadcrumbSubject || pageContent.subject"
        :subject-domains="(breadcrumbDomain && [breadcrumbDomain]) || pageContent.subject_domains"
        :grades="pageContent.grades"
        :title="pageContent.title"
      />

      <div class="row">
        <div class="col-sm-8">
          <AnchorPageLabel v-if="!hasAccordionObjectives" />
          <h1 class="anchor-page-title">
            {{ pageContent.title }}
            <small
              class="text-muted"
              v-if="pageContent.uuid && (draft || !pageContent.published)"
            >(Draft)</small>
            <a
              v-if="editorVisibility && !pageContent.access_grade_level_uuid"
              :href="editGoalUrl"
              class="btn btn-sm btn-default"
            ><i class="fa fa-pencil" /> Edit</a
            >
            <router-link
              class="btn btn-sm btn-default"
              v-if="editorVisibility && pageContent.access_grade_level_uuid"
              :to="{
                name: 'EditAnchorAccessPoints',
                params: { uuid: pageContent.access_grade_level_uuid },
              }"
            ><i class="fa fa-pencil" /> Edit</router-link
            >
          </h1>

          <AccessPointTabs
            v-if="showAccessPointTabs"
            :active-access-point="pageContent.access_point_level"
            :access-point-uuids="accessPointsLevelUuidMap"
          />

          <h2 v-if="!hasAccordionObjectives">Annual Target</h2>
          <BigGoal
            :footer="showAnnualGoalFooter ? true : false"
            :footer2="hasAccordionObjectives"
            :footer3="isPremium && aiFeatureVisibility"
            :apply-new-border-styles="hasAccordionObjectives"
            :event-context="{ obj_level: 'Annual' }"
          >
            <template slot="body">
              <ReviewResults
                v-if="!autoReviewDismissed && (autoReviewLoading || autoReviewError || autoReviewResult)"
                title="Goal Review"
                :audit-results="autoReviewResult || []"
                :is-loading="autoReviewLoading"
                :error-message="autoReviewError || ''"
                :criteria-items="filteredGoalsCriteria"
                @close="closeReviewResults"
              />
              <CopyTrackWrapper
                type="annual_target"
                level="Annual"
                class="annual-target-text"
                :class="{ 'anchor-page-wiki-text': hasAccordionObjectives }"
                :title="pageContent.title"
                :uuid="pageContent.uuid"
                :demo="pageContent.demo"
              >
                <div v-if="editorVisibility && pageContent.customized" class="badge">Customized</div>
                <div v-if="fetchingCustomization">
                  <LoadingSkeletonRow :possible-widths="['90%', '85%', '95%']" height="18px" />
                  <LoadingSkeletonRow :possible-widths="['80%', '88%', '92%']" height="18px" />
                  <LoadingSkeletonRow :possible-widths="['75%', '82%', '87%']" height="18px" />
                </div>
                <template v-else-if="!accessInfo.blur">
                  <EditableWikiTextArea
                    v-if="pageContent.goal_text"
                    v-model="pageContent.goal_text"
                    :key="originalGoalTexts[pageContent.access_point_level]"
                    :reset-value="originalGoalTexts[pageContent.access_point_level]"
                    :strategies="strategyMap"
                    copy-tooltip-text="Goal Copied"
                    @textEdited="processTextEdit"
                    @textReset="processTextReset"
                    :object-id="pageContent.uuid"
                  >
                    <template slot="translateButton">
                      <span v-if="aiFeatureVisibility" class="separator"><TranslateButton @translationLanguageChanged="(lang) => {processLanguageChange(lang)}" /></span>
                    </template>
                  </EditableWikiTextArea>
                </template>
                <TranslationContainer
                  v-if="readyToTranslate"
                  :goal_title="pageContent.title"
                  :goal_text="pageContent.goal_text"
                  :goal_uuid="pageContent.uuid"
                  :edited="goalTextEdited"
                  :external-open.sync="openTranslationContainer"
                  :requested-language="translationLanguage"
                />
              </CopyTrackWrapper>
            </template>

            <template slot="footer" v-if="hasPMAs">
              <ResourceListContainer
                :uuids="pageContent.goal_resources"
                :is-indented="hasAccordionObjectives"
                :show-resource-tile="hasAccordionObjectives"
                :object-id="pageContent.uuid"
              />
            </template>

            <template v-if="hasAccordionObjectives" v-slot:footer2>
              <GoalExampleContent
                @toggle="toggledQuarterlyObjectives"
                :initially-expand="true"
                :is-indented="hasAccordionObjectives"
                key="accordion-objectives"
              >
                <template v-slot:label>
                  <img
                    src="~@/assets/quarterly-objectives-icon.svg"
                    class="objectives-icon--button"
                    alt=""
                    aria-hidden="true"
                  >Objectives
                </template>
                <ReviewResults
                  v-if="hasObjectivesCriteria && (autoReviewLoading || autoReviewError || (objectivesReviewResult && objectivesReviewResult.audit_results))"
                  title="Objectives Review"
                  :audit-results="objectivesReviewResult ? objectivesReviewResult.audit_results : []"
                  :is-loading="autoReviewLoading"
                  :error-message="autoReviewError || ''"
                  :criteria-items="filteredObjectivesCriteria"
                  @close="closeObjectivesReviewResults"
                />
                <AccordionObjectivesList
                  :access-info="accessInfo"
                  :print-permissions="actionPermissions.printAssessment"
                  :page-uuid="pageContent.uuid"
                  :objectives="pageContent.objectives"
                  :levelled-objective-texts="editableObjectives"
                  @objective:edited="objectiveTextEdited"
                  :editable="!accessInfo.blur"
                  :fetching="fetchingCustomization"
                />
              </GoalExampleContent>
            </template>

            <template v-slot:footer2 v-else-if="pageContent.goal_example_text">
              <GoalExampleContent @toggle="clickedExampleContent">
                <CopyTrackWrapper
                  type="goal-example"
                  :title="pageContent.title"
                  :uuid="pageContent.uuid"
                  :demo="pageContent.demo"
                >
                  <wiki-text :text="pageContent.goal_example_text" :locked="accessInfo.blur" />
                </CopyTrackWrapper>
              </GoalExampleContent>
            </template>

            <template v-slot:footer3 v-if="isPremium && aiFeatureVisibility">
              <GoalExampleContent
                @toggle="toggledSDIStrategies"
                :initially-expand="true"
                :is-indented="hasAccordionObjectives"
                key="sdi-strategies"
              >
                <template v-slot:label>
                  <i class="fa fa-solid fa-wrench sdis-icon--button" aria-hidden="true" />Specially
                  Designed Instructional Strategies
                </template>
                <SDIStrategies
                  :goal-text="pageContent.plain_goal_text"
                  :goal-grades="pageContent.grades"
                  :class="{ 'acc-objectives-indent': hasAccordionObjectives }"
                />
              </GoalExampleContent>
            </template>
          </BigGoal>

          <div class="row" v-if="accessInfo.blur">
            <div class="col-sm-10 col-sm-offset-1">
              <LimitAccessAlert :access-info="accessInfo" />
            </div>
          </div>

          <template v-if="!hasAccordionObjectives">
            <h2>Quarterly Objectives</h2>
            <AnchorPageObjectives
              :items="pageContent.assessment_items"
              :access-info="accessInfo"
              :print-permissions="actionPermissions.printAssessment"
              :page-uuid="pageContent.uuid"
              :objectives="pageContent.objectives"
              :levelled-objective-texts="editableObjectives"
              @objective:edited="objectiveTextEdited"
              :editable="!accessInfo.blur"
              :objective-resource-uuids="pageContent.objective_resources"
              :fetching-customization="fetchingCustomization"
            />
            <div class="row" v-if="accessInfo.blur">
              <div class="col-sm-10 col-sm-offset-1">
                <LimitAccessAlert :access-info="accessInfo" />
              </div>
            </div>
          </template>
        </div>
        <div class="col-sm-4">
          <div class="row">
            <div class="col-sm-12 col-xs-6">
              <div :class="{ 'library-button': hasAccordionObjectives }">
                <SaveToLibraryButton
                  :content-uuid="pageContent.uuid"
                  :content-payload="savedAnchorPayloadWithContentProfile"
                  :user-uuid="userUuid"
                  :action-permissions="actionPermissions.save"
                  @saved:start="resetSaveStatus"
                  @saved:error="handleSaveError"
                  @saved:success="handleSaveSuccess"
                />
                <AutoReviewButton
                  v-if="showGoalReview"
                  :content-uuid="pageContent.uuid"
                  :criteria-set-name="contentReviewCriteriaSetName"
                  @click="openAutoReviewModal"
                />
              </div>

              <AutoReviewModal
                :is-open="isAutoReviewModalOpen"
                :criteria-set-name="contentReviewCriteriaSetName"
                :criteria-by-type="contentReviewCriteria"
                :goal-grades="pageContent.grades"
                :goal-subjects="pageContent.subject_alignments?.map(a => a.subject) || []"
                @close="closeAutoReviewModal"
                @review="handleAutoReview"
              />

              <transition name="fade">
                <alert
                  role="status"
                  type="success"
                  class="action-alert text-center"
                  :dismissible="true"
                  @dismissed="resetSaveStatus"
                  :duration="5000"
                  v-if="saveToLibraryStatus.savedSuccessInfo.name"
                >
                  Saved to
                  <span class="fs-block">{{ saveToLibraryStatus.savedSuccessInfo.name }}</span
                  >!
                  <router-link
                    :to="{
                      name: 'SavedAnchorPage',
                      params: { uuid: saveToLibraryStatus.savedSuccessInfo.uuid },
                    }"
                  >View saved anchor page</router-link
                  >
                </alert>
              </transition>
              <transition name="fade">
                <alert
                  type="danger"
                  class="action-alert text-center"
                  :dismissible="true"
                  @dismissed="resetSaveStatus"
                  v-if="saveToLibraryStatus.savedErrorMessage"
                >
                  {{ saveToLibraryStatus.savedErrorMessage }}
                </alert>
              </transition>
            </div>
          </div>

          <ProgressMonitoringPanel
            v-if="pageContent.title && pageContent.uuid && !hasPMAs"
            :content-type="'anchor'"
            :content-title="pageContent.title"
            :content-uuid="pageContent.uuid"
          />

          <template v-if="fetchingStandards || fetchingStaircase">
            <p class="text-muted"><i class="fa fa-spin fa-circle-o-notch" /> Loading...</p>
          </template>

          <template v-if="keyStandard">
            <h2>{{ keyStandard.standard_set }}</h2>
            <CopyTrackWrapper
              type="standard"
              :title="pageContent.title"
              :uuid="pageContent.uuid"
              :demo="pageContent.demo"
            >
              <PowerStandard v-if="!hasAccordionObjectives" :standard="keyStandard" />
              <StandardsAccordion
                v-else
                :standards="keyStandardArray"
                :should-expand-first-standard="true"
              />
            </CopyTrackWrapper>
          </template>

          <GoalContentPageStandards
            :show-edit-link="editorVisibility"
            :content-profile-id="profileId"
            :grouped-standards="groupedSidebarStandards"
            :should-expand-first-standard="!hasAccordionObjectives"
          />

          <template v-if="staircaseContent && staircaseContent.length > 0">
            <h2 :class="{ 'sidebar-section': hasAccordionObjectives }">Standard Staircase</h2>
            <CopyTrackWrapper
              type="staircase"
              :title="pageContent.title"
              :uuid="pageContent.uuid"
              :demo="pageContent.demo"
            >
              <Staircase
                :page-uuid="pageContent.uuid"
                :grades="pageContent.grades"
                :goal-staircase="staircaseContent"
                :access-point-level="pageContent.access_point_level"
                :access-point-grade-level-uuid="pageContent.access_grade_level_uuid"
                :show-anchor-page-label="!hasAccordionObjectives"
              />
            </CopyTrackWrapper>
          </template>

          <template v-if="strategies && strategies.length > 0">
            <h2 :class="{ 'sidebar-section': hasAccordionObjectives }">Referenced Strategies</h2>
            <StrategyMediaList :strategies="strategies" />
          </template>
        </div>
      </div>

      <SubmitFeedback
        :title="pageContent.title"
        :uuid="pageContent.uuid"
        type="Anchor Page"
        :subject="pageContent.subject"
      />
    </div>

    <div class="print-message">This page is intentionally left blank.</div>
  </CopyTrackWrapper>
</template>

<script>
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex'
import {
  anchorPageMixin,
  saveCustomContentWithContentProfileMixin,
  trackAnchorContentEditedMixin,
  autoReviewMixin
} from '@/components/goals/common/goal_content_mixin'
import AccessPointTabs from '@/components/goals/AccessPointTabs'
import EditableWikiTextArea from '@/components/EditableWikiTextArea'
import ContentPageBreadcrumbs from '@/components/goals/ContentPageBreadcrumbs'
import StandardsAccordion from '@/components/goals/StandardsAccordion'
import AnchorPageObjectives from '@/components/goals/AnchorPageObjectives'
import ProgressMonitoringPanel from '@/components/goals/ProgressMonitoringPanel'
import SaveToLibraryButton from '@/components/goals/SaveToLibraryButton'
import AutoReviewButton from '@/components/goals/AutoReviewButton'
import AutoReviewModal from '@/components/goals/AutoReviewModal'
import ReviewResults from '@/components/goals/ReviewResults'
import LimitAccessAlert from '@/components/LimitAccessAlert'
import StrategyMediaList from '@/components/strategies/StrategyMediaList'
import PremiumRequired from '@/components/PremiumRequired'
import ErrorPage from '@/components/ErrorPage'
import LoadingSkeletonRow from '@/components/LoadingSkeletonRow'
import { toolkitUrls } from '@/links'
import { setTitle } from '@/util/title'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { shouldShowGoalReview } from '@/util/goals'
import { pmaSvg } from '@/icons'
import SDIStrategies from '@/components/sdistrategies/SDIStrategies.vue'
import TranslateButton from '@/components/goals/TranslateButton.vue'
import TranslationContainer from '@/components/goals/TranslationContainer.vue'
import { AccessPointsUtil } from '@/components/goals/common/access_points'

export default {
  name: 'AnchorPage',
  mixins: [
    anchorPageMixin,
    saveCustomContentWithContentProfileMixin,
    trackAnchorContentEditedMixin,
    autoReviewMixin,
  ],
  components: {
    SDIStrategies,
    AccessPointTabs,
    ContentPageBreadcrumbs,
    EditableWikiTextArea,
    LimitAccessAlert,
    SaveToLibraryButton,
    AutoReviewButton,
    AutoReviewModal,
    ReviewResults,
    ProgressMonitoringPanel,
    AnchorPageObjectives,
    StrategyMediaList,
    PremiumRequired,
    StandardsAccordion,
    ErrorPage,
    TranslateButton,
    TranslationContainer,
    LoadingSkeletonRow,
  },
  props: {
    uuid: {
      required: true,
      type: String,
    },
    draft: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      editableObjectives: [],
      pmaSvg: pmaSvg,
      originalGoalTexts: {},
      goalTextEdited: false,
      translationLanguage: '',
      readyToTranslate: false,
      openTranslationContainer: false,
      tabChanged: false,
    }
  },
  computed: {
    ...mapGetters('user', ['authInfo', 'isPremium']),
    ...mapState('user', { userUuid: (state) => state.user.uuid }),
    ...mapState('goaldetail', ['saveToLibraryStatus', 'strategies', 'fetchingCustomization', 'fetching']),
    ...mapGetters('customanchor', ['savedAnchorPayload']),
    ...mapGetters('visibility', ['editorVisibility', 'aiFeatureVisibility', 'betaVisibility']),
    ...mapGetters('goaldetail', ['premiumBlock', 'goalLoadError']),
    ...mapGetters('anchorpage', ['isTranslationActivatedForAccessPoint']),
    ...mapGetters('goaldetail', ['customizedGoal']),
    savedAnchorPayloadWithContentProfile() {
      return this.contentPayloadWithContentProfile(this.savedAnchorPayload)
    },
    editGoalUrl() {
      return toolkitUrls.editorLinks.editGoal(this.uuid)
    },
    translationActiveForCurrentAccessPoint() {
      return this.isTranslationActivatedForAccessPoint(this.pageContent.uuid)
    },
    accessPointsValid() {
      const map = this.accessPointsLevelUuidMap
      return map &&
        AccessPointsUtil.orderedValues.every(ap => map[ap])
    },
    showAccessPointTabs() {
      return this.accessPointsValid &&
        (this.areAccessPointsPublished || (this.pageContent.access_grade_level_uuid && this.editorVisibility))
    },
    showGoalReview() {
      return !this.fetching && shouldShowGoalReview({
        contentReviewCriteriaSetName: this.contentReviewCriteriaSetName,
        goalCustomized: this.customizedGoal,
        aiFeatureVisibility: this.aiFeatureVisibility,
        goalUuid: this.pageContent.uuid,
        contentReviewCriteria: this.contentReviewCriteria
      })
    }
  },
  methods: {
    ...mapMutations('goaldetail', [
      'setSaveToLibrarySuccess',
      'setSaveToLibraryError',
      'resetSaveStatus',
    ]),
    ...mapMutations('anchorpage', [
      'activateTranslationForAccessPoint',
      'deactivateTranslationForAccessPoint',
      'deactivateTranslationForAllAccessPoints'
    ]),
    ...mapActions('contentProfile', ['fetchContentReviewCustomization']),
    ...mapActions('customanchor', ['initializeCustomAnchorPage', 'fetchGoalTranslationLanguages']),
    ...mapActions('goaldetail', ['fetchAllResources']),
    processLanguageChange(lang) {
      this.translationLanguage = lang
      this.openTranslationContainer = true
    },
    processTextEdit(goalText) {
      this.annualTextEdited(goalText)
      this.goalTextEdited = true
    },
    processTextReset(goalText) {
      this.annualTextEdited(goalText)
      this.goalTextEdited = false
      this.openTranslationContainer = false
    },
    loadPageContent() {
      this.readyToTranslate = false
      this.goalTextEdited = false
      this.resetAutoReviewState()
      return this.fetchPageContent({
        type: CONTENT_TYPES.SUPER_PAGE,
        attribute: 'uuid',
        value: this.uuid,
        params: {
          with_access_points: 1,
          prefer_draft: this.draft ? 1 : 0,
          content_profile: this.profileId,
          beta_mode: this.betaVisibility
        },
      }).then(async () => {
        setTitle(
          `${this.pageContent.title} | ${this.pageContent.subject} Anchor Page IEP Goal and Objectives`
        )
        this.setAccessPointsForContent(this.pageContent)
        this.initializeCustomAnchorPage({
          contentUuid: this.pageContent.uuid,
          primaryGoalText: this.pageContent.goal_text,
          objectivesArray: this.pageContent.objectives,
        })
        this.fetchGoalTranslationLanguages()
        if (!this.originalGoalTexts[this.pageContent.access_point_level]) {
          this.originalGoalTexts[this.pageContent.access_point_level] = this.pageContent.goal_text
        }
        if (this.pageContent.goal_text !== this.originalGoalTexts[this.pageContent.access_point_level]) {
          this.goalTextEdited = true
        }
        this.readyToTranslate = true
        this.editableObjectives = (this.hasObjectives && this.pageContent.objectives.map((obj) => obj.text)) || []
        if (!this.tabChanged) {
          this.$sendEvent('Viewed Anchor Page', {
            standard_codes:
              this.pageContent.standard_set_refs?.map(ref => ref.standard_long_ref)
          })
          this.tabChanged = true
        }
        await this.fetchAllResources()
      })
    },
    handleSaveSuccess(savedGoal) {
      this.setSaveToLibrarySuccess({
        name: savedGoal.student_name,
        uuid: savedGoal.uuid,
        libraryContentUrl: toolkitUrls.libraryGoalPage(savedGoal.uuid),
      })
    },
    handleSaveError(errorMessage) {
      this.setSaveToLibraryError(errorMessage)
    },
  },
  eventContext() {
    return {
      goal_breadcrumb_subject: this.breadcrumbSubject || '',
      goal_breadcrumb_domain: this.breadcrumbDomain || '',
      goal_subject_alignments: this.pageContent.subject_alignments,
      goal_subject: this.pageContent.subject,
      goal_subject_domains: this.pageContent.subject_domains,
      goal_grades: this.pageContent.grades,
      goal_uuid: this.pageContent.uuid,
      goal_title: this.pageContent.title,
      goal_standards:
        this.pageContent.standard_set_refs &&
        this.pageContent.standard_set_refs.map((setRef) => setRef.standard_long_ref),
      goal_author_id: this.pageContent.author_id,
      goal_is_anchor: this.pageContent.super_page,
      goal_has_objectives: this.hasObjectives,
      goal_objs_have_resources: this.hasObjectives && !this.hasAccordionObjectives,
      goal_is_library: false,

      goal_has_access_points: this.areAccessPointsPublished,
      goal_access_point_level: this.pageContent.access_point_level,
      goal_access_grade_level_uuid: this.pageContent.access_grade_level_uuid,
      criteria_label: this.contentReviewCriteriaSetName || 'default',
      is_custom_criteria_set: !!this.contentReviewCriteriaSetName,
    }
  },
  watch: {
    'pageContent.uuid': {
      handler(newUuid) {
        this.openTranslationContainer = this.isTranslationActivatedForAccessPoint(newUuid)
      },
      immediate: true
    },
    fetchingCustomization(isFetching, wasFetching) {
      // When customization finishes loading, update originalGoalTexts and editableObjectives
      if (wasFetching && !isFetching) {
        if (this.pageContent.objectives) {
          this.editableObjectives = (this.hasObjectives && this.pageContent.objectives.map((obj) => obj.text)) || []
        }
        this.originalGoalTexts[this.pageContent.access_point_level] = this.pageContent.goal_text
        this.initializeCustomAnchorPage({
          contentUuid: this.pageContent.uuid,
          primaryGoalText: this.pageContent.goal_text,
          objectivesArray: this.pageContent.objectives,
        })
      }
    },
    translationActiveForCurrentAccessPoint(val) {
      this.openTranslationContainer = val
    },
    openTranslationContainer(val) {
      if (val) {
        this.activateTranslationForAccessPoint(this.pageContent.uuid)
      } else {
        this.deactivateTranslationForAccessPoint(this.pageContent.uuid)
      }
    },
    $route: function () {
      this.loadPageContent()
    },
    profileId: function () {
      this.fetchContentReviewCustomization()
      this.$store.commit('goaldetail/setCustomizedGoal', null)
      this.loadPageContent()
    },
    betaVisibility: function () {
      this.fetchContentReviewCustomization()
    }
  },
  created() {
    this.loadPageContent()
    this.fetchContentReviewCustomization()
    this.autoReviewIsSavedGoal = false
  },
  beforeDestroy() {
    this.deactivateTranslationForAllAccessPoints()
    this.$store.commit('goaldetail/clearContent')
  }
}
</script>

<style lang="less" scoped>
@import '~@/variables.less';

.alert.anchor-page-notification {
  border-radius: 0;
  margin-top: -20px;
}

.alert.anchor-page-notification a:link,
.alert.anchor-page-notification a:hover {
  text-decoration: underline;
}
.panel-body + .panel-footer .resource-container-indentation {
  margin-top: -10px;
}
.separator {
  border-left: 1px solid #ADADAD;
  padding-left: 8px;
  line-height: 24px;
  font-size: 18px;
  float: left;
  margin-left: 8px;
}
</style>
